import { ColumnsSelection } from "drizzle-orm";
import { PgSelectBase } from "drizzle-orm/pg-core";
import {
  BuildSubquerySelection,
  JoinNullability,
  SelectMode,
  SelectResult,
} from "drizzle-orm/query-builders/select.types";
import { isEqual, isFunction } from "lodash";

import { createLogger } from "@/logger";

const log = createLogger("query");

import { useEffect, useMemo, useRef, useState } from "react";

import { flags } from "@/config";
import { summarizeQuery } from "@/db/summarizeQuery";
import { TableEventManager, TableName } from "@/models/eventing/TableEventManager";
import { useThrottledCallback } from "@/models/hooks/useThrottledCallback.ts";
import { QueryMetricsService } from "@/services/QueryMetricsService";

const DEFAULT_THROTTLE_INTERVAL_MS = 250;

function extractTablesFromPgSelect<
  TTableName extends string,
  TSelection extends ColumnsSelection,
  TSelectMode extends SelectMode,
  TNullabilityMap extends Record<string, JoinNullability> = {},
  TDynamic extends boolean = false,
  TExcludedMethods extends string = never,
  // biome-ignore lint/suspicious/noExplicitAny: need to adhere to Drizzle's interface
  TResult extends any[] = SelectResult<TSelection, TSelectMode, TNullabilityMap>[],
  TSelectedFields extends ColumnsSelection = BuildSubquerySelection<TSelection, TNullabilityMap>,
>(
  query: {
    // AnyPgSelect marks getUsedTables as @internal, so convince typescript it exists but test for its actual presence
    getUsedTables?: () => string[];
  } & PgSelectBase<
    TTableName,
    TSelection,
    TSelectMode,
    TNullabilityMap,
    TDynamic,
    TExcludedMethods,
    TResult,
    TSelectedFields
  >,
  debugName?: string,
): readonly TableName[] {
  if (!isFunction(query.getUsedTables)) {
    log.error(`[useReactiveQuerySelect][${debugName ?? "unknown"}] getUsedTables is not a function`, {
      debugName,
      query,
      sql: query.toSQL().sql,
    });
    throw new Error(`[useReactiveQuerySelect][${debugName ?? "unknown"}] getUsedTables is not a function`);
  }

  const found = new Set(query.getUsedTables());

  // Normalize: drop schema prefixes (e.g., "public.foo" -> "foo")
  const normalized = Array.from(found)
    .map((n) => (n.includes(".") ? (n.split(".").pop() ?? n) : n))
    .filter((n): n is string => !!n && n.length > 0);

  return normalized as unknown as readonly TableName[];
}

export function useReactiveSelectQuery<
  Params extends unknown[],
  T extends string | number | symbol,
  TTableName extends string | undefined,
  TSelection extends ColumnsSelection,
  TSelectMode extends SelectMode,
  TNullabilityMap extends Record<string, JoinNullability> = TTableName extends string
    ? Record<TTableName, "not-null">
    : {},
  TDynamic extends boolean = false,
  TExcludedMethods extends string = never,
  // biome-ignore lint/suspicious/noExplicitAny: need to adhere to Drizzle's interface
  TResult extends any[] = SelectResult<TSelection, TSelectMode, TNullabilityMap>[],
  TSelectedFields extends ColumnsSelection = BuildSubquerySelection<TSelection, TNullabilityMap>,
>(
  queryFactory: (
    ...params: Params
  ) => Omit<
    PgSelectBase<
      TTableName,
      TSelection,
      TSelectMode,
      TNullabilityMap,
      TDynamic,
      TExcludedMethods,
      TResult,
      TSelectedFields
    >,
    T
  >,
  params: Params,
  debugName?: string,
  throttleIntervalMs = DEFAULT_THROTTLE_INTERVAL_MS,
): TResult | null {
  const [currentResult, setCurrentResult] = useState<TResult | null>();

  // Only update params if they've changed. Otherwise React will treat each array as a new reference and will re-run the
  // query every time.
  //
  // See https://stackoverflow.com/a/66653729
  const currentParams = useRef<unknown[]>(params);

  if (!isEqual(params, currentParams.current)) {
    currentParams.current = params;
  }

  const builtQuery = useMemo(() => {
    return queryFactory(...params) as PgSelectBase<
      TTableName,
      TSelection,
      TSelectMode,
      TNullabilityMap,
      TDynamic,
      TExcludedMethods,
      TResult,
      TSelectedFields
    >;
  }, [queryFactory, params]);

  const querySql = useMemo(() => builtQuery.toSQL().sql, [builtQuery]);
  const querySummary = useMemo(() => summarizeQuery(querySql), [querySql]);

  const performQuery = (tableName: TableName) => {
    const startTime = flags.debugMode ? Date.now() : 0;
    log.debug(
      `[useReactiveQuerySelect][${new Date().toISOString()}][EXECUTION][${debugName ?? querySummary}] Executing reactive query because table ${tableName} was updated`,
      { querySummary, debugName, tableName, querySql, time: startTime },
    );
    builtQuery
      .then((r) => {
        setCurrentResult(r);

        if (flags.debugMode) {
          const duration = Date.now() - startTime;
          QueryMetricsService.recordQueryExecution({
            queryName: debugName ?? querySummary,
            queryType: "reactive-select",
            duration,
            querySummary,
            triggeringTable: tableName,
          });
        }
      })
      .catch((error: unknown) => {
        // Without this catch, a rejected reactive query (e.g. an exhausted PGlite portal
        // retry from `installPortalRetry`) surfaces as an unhandled promise rejection and
        // tears down the React tree. The next subscription notification will retry the
        // query, so swallow-and-log here.
        log.error(`[useReactiveQuerySelect][${debugName ?? querySummary}] Reactive query failed`, {
          querySummary,
          debugName,
          tableName,
          querySql,
          error,
        });
      });
  };

  const throttleQuery = useThrottledCallback(performQuery, throttleIntervalMs);

  useEffect(() => {
    const tables = builtQuery ? extractTablesFromPgSelect(builtQuery, debugName) : [];

    if (tables.length > 0) {
      throttleQuery(tables[0]);
    }

    return TableEventManager.subscribe(tables, (tableName) => {
      throttleQuery(tableName);
    });
  }, [querySql, currentParams.current]);

  return currentResult;
}
